import React from "react";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BuildIcon from "@mui/icons-material/Build";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ErrorIcon from "@mui/icons-material/Error";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import SettingsIcon from "@mui/icons-material/Settings";
import SyncIcon from "@mui/icons-material/Sync";
import {
  Box,
  Chip,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  Stack,
  Tooltip,
  Typography,
  alpha,
  keyframes,
  useTheme,
} from "@mui/material";

// ── Types ──────────────────────────────────────────────────────────────────────

export type StageKey = "summary" | "setup" | "build" | "deploy";

export interface StageInfo {
  key: StageKey;
  label: string;
  status:
    "completed" | "in_progress" | "failed" | "pending" | "queued" | "idle";
}

export interface JobInfo {
  name: string;
  status:
    "completed" | "in_progress" | "failed" | "pending" | "queued" | "idle";
}

export interface StagesSidebarProps {
  stages: StageInfo[];
  selectedStage: StageKey;
  onSelectStage: (stage: StageKey) => void;
  onBackToRuns: () => void;
  runLabel?: string;
  /** When provided, sidebar switches to "jobs" mode showing task/job items
   *  for the selected phase instead of the stages list. */
  phaseJobs?: JobInfo[];
  /** The label for the phase when in jobs mode (e.g. "Build" or "Deploy") */
  phaseLabel?: string;
  /** The currently selected job name (for jobs mode) */
  selectedJob?: string | null;
  /** Callback when a job is selected (for jobs mode) */
  onSelectJob?: (jobName: string) => void;
  /** Callback to go back from jobs mode to stages mode */
  onBackToStages?: () => void;
}

// ── Constants ──────────────────────────────────────────────────────────────────

const _STAGE_ICON: Record<StageKey, React.ReactElement> = {
  summary: <DashboardIcon fontSize="small" />,
  setup: <SettingsIcon fontSize="small" />,
  build: <BuildIcon fontSize="small" />,
  deploy: <RocketLaunchIcon fontSize="small" />,
};

const STATUS_ICON: Record<string, React.ReactElement> = {
  completed: (
    <CheckCircleIcon fontSize="small" sx={{ color: "success.main" }} />
  ),
  in_progress: (
    <SyncIcon
      fontSize="small"
      sx={{
        color: "info.main",
        animation: `${keyframes`from{transform:rotate(0deg)}to{transform:rotate(360deg)}`} 1.5s linear infinite`,
      }}
    />
  ),
  failed: <ErrorIcon fontSize="small" sx={{ color: "error.main" }} />,
  pending: (
    <HourglassEmptyIcon fontSize="small" sx={{ color: "warning.main" }} />
  ),
  queued: <HourglassEmptyIcon fontSize="small" sx={{ color: "grey.500" }} />,
  idle: <HourglassEmptyIcon fontSize="small" sx={{ color: "grey.400" }} />,
};

const STATUS_CHIP_COLOR: Record<
  string,
  "success" | "error" | "warning" | "info" | "default"
> = {
  completed: "success",
  in_progress: "info",
  failed: "error",
  pending: "warning",
  queued: "default",
  idle: "default",
};

// ── Component ──────────────────────────────────────────────────────────────────

export function StagesSidebar({
  stages,
  selectedStage,
  onSelectStage,
  onBackToRuns,
  runLabel,
  phaseJobs,
  phaseLabel,
  selectedJob,
  onSelectJob,
  onBackToStages,
}: StagesSidebarProps): React.ReactElement {
  const theme = useTheme();
  const isJobsMode = phaseJobs && phaseJobs.length > 0;

  return (
    <Box
      sx={{
        width: 240,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        bgcolor: alpha(theme.palette.primary.main, 0.02),
        overflow: "hidden",
      }}
    >
      {/* Back button + label */}
      <Box
        sx={{
          px: 2,
          py: 1,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          {isJobsMode ? (
            <Tooltip title={`Back to stages`}>
              <IconButton size="small" onClick={onBackToStages}>
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Back to runs list">
              <IconButton size="small" onClick={onBackToRuns}>
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Typography
            variant="caption"
            fontWeight={600}
            color="text.secondary"
            sx={{ textTransform: "uppercase", letterSpacing: 1 }}
          >
            {isJobsMode
              ? `${phaseLabel ?? "Phase"} Tasks`
              : (runLabel ?? "Run")}
          </Typography>
        </Stack>
      </Box>

      {/* List: either stages or jobs */}
      <List disablePadding sx={{ flex: 1, overflow: "auto" }}>
        {isJobsMode
          ? phaseJobs.map((job) => {
              const isSelected = job.name === selectedJob;
              const statusIcon = STATUS_ICON[job.status] ?? STATUS_ICON.pending;
              return (
                <ListItemButton
                  key={job.name}
                  selected={isSelected}
                  onClick={() => onSelectJob?.(job.name)}
                  sx={{
                    py: 1,
                    px: 2,
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.04)}`,
                    "&:last-child": { borderBottom: "none" },
                    "&:hover": {
                      bgcolor: alpha(theme.palette.primary.main, 0.04),
                    },
                    "&.Mui-selected": {
                      bgcolor: alpha(theme.palette.primary.main, 0.06),
                      borderLeft: `3px solid ${theme.palette.primary.main}`,
                      pl: 1.625,
                      "&:hover": {
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                      },
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    {statusIcon}
                  </ListItemIcon>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {job.name}
                    </Typography>
                    <Chip
                      label={job.status.replace("_", " ")}
                      size="small"
                      color={STATUS_CHIP_COLOR[job.status] ?? "default"}
                      variant="outlined"
                      sx={{ height: 16, fontSize: "0.55rem", mt: 0.25 }}
                    />
                  </Box>
                </ListItemButton>
              );
            })
          : stages.map((stage) => {
              const isSelected = stage.key === selectedStage;
              const statusIcon =
                STATUS_ICON[stage.status] ?? STATUS_ICON.pending;
              return (
                <ListItemButton
                  key={stage.key}
                  selected={isSelected}
                  onClick={() => onSelectStage(stage.key)}
                  sx={{
                    py: 1,
                    px: 2,
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.04)}`,
                    "&:last-child": { borderBottom: "none" },
                    "&:hover": {
                      bgcolor: alpha(theme.palette.primary.main, 0.04),
                    },
                    "&.Mui-selected": {
                      bgcolor: alpha(theme.palette.primary.main, 0.06),
                      borderLeft: `3px solid ${theme.palette.primary.main}`,
                      pl: 1.625,
                      "&:hover": {
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                      },
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    {statusIcon}
                  </ListItemIcon>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {stage.label}
                    </Typography>
                    <Chip
                      label={stage.status.replace("_", " ")}
                      size="small"
                      color={STATUS_CHIP_COLOR[stage.status] ?? "default"}
                      variant="outlined"
                      sx={{ height: 16, fontSize: "0.55rem", mt: 0.25 }}
                    />
                  </Box>
                </ListItemButton>
              );
            })}
      </List>
    </Box>
  );
}
